import React, { useId } from 'react';
import styled, { css } from 'styled-components';

import Fieldset from '@rover/components/src/formFields/Fieldset';
import FormValidationError from '@rover/components/src/formFields/FormValidationError';
import A11yHidden from '@rover/kibble/styles/A11yHidden';
import { HorizontalLayout } from '@rover/react-lib/src/components/layouts';

import FancyRadio, { Props as FancyRadioProps } from './FancyRadio';
import type { CommonProps } from './FancyRadioGroup.types';

export type Option = Omit<
  FancyRadioProps,
  | 'checked'
  | 'name'
  | 'onChange'
  | 'disabled'
  | 'optionWidth'
  | 'styleAsRows'
  | 'hideLabel'
  | 'validationType'
  | 'label'
> & {
  value: string;
  disabled?: boolean;
  hideLabel?: boolean;
  'data-qa-id'?: string;
  label: React.ReactNode | string;
};

export type Props = CommonProps & {
  name: string;
  options: Option[];
  optionWidth?: string;
  styleAsRows?: boolean;
  subtitle?: string | null;
};

export const Wrapper = styled.div<{
  styleAsRows: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${(props) => (props.styleAsRows ? 'column' : 'initial')};
  align-items: flex-start;
`;

type TitleProps = {
  hideLabel?: Props['hideLabel'];
  styleAsRows?: Props['styleAsRows'];
};

export const Title = styled.legend<TitleProps>`
  flex: 0 0 100%;
  border: 0;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.space['4x']};
  ${(props) => (props.hideLabel ? A11yHidden : '')};

  ${(props) =>
    !props.hideLabel && !props.styleAsRows
      ? css`
          margin-right: ${({ theme }) => theme.space['4x']};
        `
      : ''};
`;

export const SubTitle = styled(HorizontalLayout)`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.paragraph['100'].fontSize};
  font-weight: ${({ theme }) => theme.paragraph['100'].fontWeight};
  margin-bottom: ${({ theme }) => theme.space['1x']};
`;

const FancyRadioGroup = ({
  disabled = false,
  error = null,
  hideLabel = false,
  name,
  onChange,
  options,
  optionWidth = '',
  styleAsRows = false,
  title,
  subtitle,
  value,
  ...other
}: Props): JSX.Element => {
  const titleId = useId();
  return (
    <Fieldset aria-labelledby={titleId}>
      <FormValidationError message={error}>
        <Wrapper {...other} styleAsRows={styleAsRows}>
          <Title hideLabel={hideLabel} id={titleId}>
            {title}
          </Title>
          {subtitle && <SubTitle>{subtitle}</SubTitle>}
          {options.map((option) => (
            <FancyRadio
              {...option}
              label={option.label}
              key={`${name}-${option.value}`}
              id={`${name}-${option.value}`}
              data-qa-id={`${name}-${option.value}`}
              name={`${name}-${titleId}`}
              disabled={disabled || option.disabled}
              checked={option.value === value}
              optionWidth={optionWidth}
              styleAsRows={styleAsRows}
              onChange={onChange}
              validationType="popover"
            />
          ))}
        </Wrapper>
      </FormValidationError>
    </Fieldset>
  );
};

export default FancyRadioGroup;
